<script>
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import ReservationResource from '@/resources/reservation'
import ResourceAttribute from '@/components/ResourceAttribute.vue'
import { dateFormatted, dateFormattedPretty, timeDateFormattedPretty, timestampTimeFormattedPretty, timestampDayFormattedPretty, dateToSlotForSingleDay } from '../util'

export default {
    name: 'CalendarReservationsPlanner',
    components: {
        ResourceAttribute,
    },
    computed: {
        ...mapGetters(['serverConfig']),
        showDatePicker() {
            return !this.forDate
        },
        bookDateFormattedPretty() {
            return dateFormattedPretty(this.bookDate)
        },
        bookDateFormatted() {
            return dateFormatted(this.bookDate)
        },
        bookTimeDateFormattedPretty() {
            if (!this.bookTimeSlotResource) {
                return this.bookDateFormattedPretty
            }
            return timeDateFormattedPretty(this.bookTimeSlotResource.dateTime)
        },
        selectedReservationsDeletable() {
            return this.selectedReservations.filter((reserv) => reserv.id)
        },
        selectedReservationsBlackoutable() {
            return this.selectedReservations.filter((reserv) => !reserv.id)
        },
        reservations() {
            const selectedBookDate = this.bookDate
            const selectedDayExistingResources = this.reservationResources.filter((resource) => {
                const resourceDate = new Date(resource.get('time'))
                const bookYearIsSame = selectedBookDate.getFullYear() == resourceDate.getFullYear()
                const bookMonthIsSame = selectedBookDate.getMonth() == resourceDate.getMonth()
                const bookDateIsSame = selectedBookDate.getDate() == resourceDate.getDate()
                return true //bookYearIsSame && bookMonthIsSame && bookDateIsSame
            })

            return this.availReservationResources.concat(selectedDayExistingResources).sort((resLeft, resRight) => {
                return parseInt(resLeft.get('slot')) - parseInt(resRight.get('slot'))
            })
        },
        reservationNotes() {
            if (!this.reservationNotesExtra) {
                return this.reservationNotesReasonText
            }

            return `${this.reservationNotesReasonText} - ${this.reservationNotesExtra}`
        },
        reservationNotesReasonText() {
            return this.reservationNotesReasons[this.reservationNotesReason]
        },
        timeContext() {
            let next = null
            let current = null

            for (let key in this.reservations) {
                const resource = this.reservations[key]
                const timeContext = resource.getTimeContext()

                if (timeContext.isNext) {
                    next = resource
                } else if (timeContext.isCurrent) {
                    current = resource
                }
            }

            return {
                next,
                current,
            }
        },
    },
    watch: {
        forDate() {
            this.bookDate = this.forDate
            this.bookDateChange()
        },
    },
    props: {
        calendarId: {
            type: String,
        },
        forDate: {
            type: [String, Date],
        },
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, 0)
        const minDate = new Date(now.getFullYear(), now.getMonth() - 2, 0)
        return {
            calendarResource: null,
            reservationResources: [],
            availReservationResources: [],
            selectedReservations: [],
            selectedReservationDetails: null,
            reservationNotesReasons: {
                WALK_IN: 'Walk-in',
                SERVICE_COMP: 'Service Comp',
                MAINT: 'Maintenance',
                OTHER: 'Other',
            },
            reservationNotesReason: 'WALK_IN',
            reservationNotesPaid: false,
            reservationNotesExtra: '',
            reservationUnlock: true,
            costCents: '$0.00',
            loading: true,
            loadingReservationTimes: false,
            refreshIntervalId: null,
            refreshIntervalMs: 6e5,
            bookDate: this.forDate || today,
            dateMin: minDate,
            dateMax: maxDate,
            now,
        }
    },
    methods: {
        timestampTimeFormattedPretty,
        timestampDayFormattedPretty,
        dateDisabled(ymd, date) {
            return false // date.getDay() === 0 || date.getDay() === 3
        },
        refreshBookDate() {
            if (this.$refs.calendar) {
                this.$refs.calendar.$emit('input', this.bookDate)
            }
        },
        seatingGroupIdFor(reserv) {
            if (!reserv || !reserv.id) {
                return null
            }
            const readonly = reserv.attributes && reserv.attributes.readonly_values
            return reserv.get('seating_group') || (readonly && readonly.seating_group) || null
        },
        adminUserIdFor(reserv) {
            return reserv.get('seated_user') || reserv.attributes.user
        },
        getTableRowClassForReservation(reserv) {
            let classes = ['reservation']
            const timeContext = reserv.getTimeContext()

            if (!reserv.id) {
                classes.push('empty')
            } else if (reserv.get('is_blackout')) {
                classes.push('blackout')
            } else {
                classes.push('reserved')
            }

            if (timeContext.isCurrent) {
                classes.push('current')
            }

            if (timeContext.isNext) {
                classes.push('next')
            }

            return classes
        },
        toggleSelectAll(value) {
            if (value) {
                this.selectedReservations = [].concat(this.availReservationResources)
            } else {
                this.selectedReservations = []
            }
        },
        async refresh() {
            this.loading = true
            let thisBookDate = this.bookDate
            this.bookDate = new Date()
            this.bookDate = thisBookDate
            await this.getExistingReservations()
            await this.bookDateChange()
            this.loading = false
            this.$nextTick(() => {
                this.scrollToNextTime()
            })
        },
        async getExistingReservations() {
            this.loading = true
            let filterAttrs = { calendar: this.calendarId }

            if (this.bookDate) {
                filterAttrs['slot__startswith'] = dateToSlotForSingleDay(this.bookDate)
            }

            let reservationResources = await ReservationResource.filterAll(filterAttrs)
            this.reservationResources = reservationResources

            this.loading = false
            return this.reservationResources
        },
        async bookDateChange() {
            await this.getExistingReservations()
            this.loadingReservationTimes = true
            this.selectedReservations = []
            let availResources = await this.calendarResource.getQuote(this.bookDateFormatted)

            this.availReservationResources = availResources

            this.loadingReservationTimes = false
        },
        openBlackoutModal() {
            this.$refs.blackoutModal.show()
        },
        openDeleteModal() {
            this.$refs.deleteModal.show()
        },
        async blackoutDates() {
            if (this.reservationNotesReason == 'OTHER' && !this.reservationNotesExtra) {
                this.$root.$bvToast.toast('You must specify notes if "other" is selected', {
                    title: 'Blackout timeslots',
                    variant: 'danger',
                })

                this.$nextTick(() => {
                    this.openBlackoutModal()
                })

                return false
            }

            const promises = this.selectedReservations.map((reserv) => {
                reserv.set('is_blackout', true)
                reserv.set('is_kiosk_mode_unlock', this.reservationUnlock)
                reserv.set('notes', this.reservationNotes)

                if (this.reservationNotesReason == 'WALK_IN') {
                    reserv.set('paid', this.reservationNotesPaid)
                }

                return reserv.save().then(() => {
                    return reserv.resolveRelated()
                })
            })

            await Promise.all(promises)

            this.$root.$bvToast.toast('The selected timeslots have been blacked out', {
                title: 'Blackout Success',
                variant: 'success',
            })

            this.reservationNotesExtra = ''
            this.reservationNotesReason = 'WALK_IN'
            this.reservationNotesPaid = false
            this.refresh()
        },
        async deleteSelectedReservations() {
            let toDeletePromises = []

            for (let reservation of this.selectedReservations) {
                let idx = this.reservationResources.indexOf(reservation)
                toDeletePromises.push(reservation.delete())
                this.$delete(this.reservationResources, idx)
            }

            await Promise.all(toDeletePromises)
            await this.refresh()
        },
        async onSeatUser() {
            this.$emit('seatUser', this.selectedReservations)
        },
        onAddToGroup() {
            this.$emit(
                'addToGroup',
                this.selectedReservations.filter((reserv) => reserv.id)
            )
            this.selectedReservations = []
        },
        async onMarkPaidStatus(isPaid) {
            const toMarkPromises = this.selectedReservations.map((reserv) => {
                reserv.set('paid', isPaid)
                reserv.set('user', reserv.get('user'))
                return reserv.save().then(() => {
                    return reserv.resolveRelated()
                })
            })

            await Promise.all(toMarkPromises)
            await this.refresh()
        },
        onTimePaneScroll(e) {
            this.$emit('scroll', e.srcElement.scrollTop)
        },
        scrollToNextTime() {
            if (!this.$refs.timeTable || !this.$refs.timePane) {
                return
            }
            const currentEl = this.$refs.timeTable.getElementsByClassName('current')[0]

            if (currentEl) {
                const currentElScrollTop = currentEl.getBoundingClientRect().y
                this.scrollY(currentElScrollTop - this.$refs.timePane.offsetHeight / 2)
            }
        },
        scrollY(pixels) {
            if (this.$refs.timePane) {
                this.$refs.timePane.scrollTop = pixels
            }
        },
    },
    async mounted() {
        this.calendarResource = await CalendarResource.detail(this.calendarId)
        this.costCents = await this.calendarResource.resolveAttribute('cost_cents')
        this.getExistingReservations()
        this.loading = false
        this.$nextTick(async () => {
            await this.bookDateChange()
            await this.refreshBookDate()
            this.scrollToNextTime()
            this.refreshIntervalId = setInterval(() => this.refresh(), this.refreshIntervalMs)
        })
    },
    beforeDestroy() {
        clearInterval(this.refreshIntervalId)
    },
}
</script>

<template>
    <div>
        <b-row>
            <b-col cols="12">
                <b-button-group class="float-right">
                    <b-button size="sm" @click="() => (bookDate = new Date())" v-if="showDatePicker"><b-icon icon="calendar-fill" /> Today</b-button>
                    <b-button v-if="calendarResource" size="sm" variant="outline-secondary" :to="{ name: 'calendarControl', params: { calendarId: calendarResource.id } }" target="_blank" title="Open sim controller" v-b-tooltip.hover><b-icon icon="controller" /></b-button>
                    <b-button @click="scrollToNextTime()" size="sm" variant="outline-secondary" title="Scroll to current time" v-b-tooltip.hover><b-icon icon="clock" /></b-button>
                    <b-button size="sm" variant="outline-secondary" @click="refresh" title="Refresh" v-b-tooltip.hover><b-icon icon="arrow-repeat" /></b-button>
                    <b-dropdown size="sm" right text="With Selected" :disabled="!selectedReservations.length">
                        <b-dropdown-item @click="openBlackoutModal" :disabled="selectedReservationsBlackoutable.length !== selectedReservations.length">Blackout Selected Times</b-dropdown-item>
                        <b-dropdown-item @click="onSeatUser" :disabled="selectedReservationsBlackoutable.length !== selectedReservations.length">Seat a User Here</b-dropdown-item>
                        <b-dropdown-item @click="onAddToGroup" :disabled="!selectedReservationsDeletable.length">Add to Group</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="onMarkPaidStatus(true)" :disabled="selectedReservationsDeletable.length !== selectedReservations.length">Mark as Paid</b-dropdown-item>
                        <b-dropdown-item @click="onMarkPaidStatus(false)" :disabled="selectedReservationsDeletable.length !== selectedReservations.length">Mark as Unpaid</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="openDeleteModal" :disabled="selectedReservationsDeletable.length !== selectedReservations.length" variant="danger">Delete Reservations</b-dropdown-item>
                    </b-dropdown>
                </b-button-group>
                <h5 v-if="calendarResource">{{ calendarResource.get('name') }}</h5>
                <h6 class="text-muted">{{ timestampDayFormattedPretty(bookDate) }}</h6>
                <div class="time-context text-muted">
                    <span v-if="timeContext.current">
                        Currently Seated:
                        <b-badge v-if="timeContext.current.attributes.is_blackout" variant="dark">Blacked Out</b-badge>
                        <b-badge v-else-if="timeContext.current.isNew()" variant="success">Available</b-badge>
                        <b-badge v-else-if="!timeContext.current.isNew()" variant="primary">
                            <a :href="`${$appConfig('API_BASE_URL')}/admin/user/user/${timeContext.current.attributes.user}`" target="_blank">
                                {{ timeContext.current.attributes.user_display_name }}
                            </a>
                        </b-badge>
                    </span>

                    <span v-if="timeContext.next" class="ml-2">
                        Next Up:
                        <b-badge v-if="timeContext.next.attributes.is_blackout" variant="dark">Blacked Out</b-badge>
                        <b-badge v-else-if="timeContext.next.isNew()" variant="success">Available</b-badge>
                        <b-badge v-else-if="!timeContext.next.isNew()" variant="primary">
                            <a :href="`${$appConfig('API_BASE_URL')}/admin/user/user/${timeContext.current.attributes.user}`" target="_blank">
                                {{ timeContext.current.attributes.user_display_name }}
                            </a>
                        </b-badge>
                    </span>
                </div>
            </b-col>

            <b-col md="4" class="mt-2 mb-5" v-if="showDatePicker">
                <b-card bg-variant="light">
                    <b-calendar v-model="bookDate" block :hide-header="true" value-as-date @input="bookDateChange" ref="calendar" :date-disabled-fn="dateDisabled" selected-variant="success" today-variant="info" locale="en" :min="dateMin" :max="dateMax"></b-calendar>
                </b-card>
            </b-col>

            <b-col class="mt-2 mb-5">
                <div v-if="messages.length > 0">
                    <b-alert :variant="message.variant" show v-for="(message, i) in messages" :key="i">{{ message.text }}</b-alert>
                </div>

                <div id="time-pane-planner" @scroll="onTimePaneScroll" ref="timePane">
                    <table class="table table-hover" ref="timeTable" v-if="!loadingReservationTimes && reservations.length > 0">
                        <thead class="thead-light">
                            <tr>
                                <th width="25"><b-form-checkbox @input="toggleSelectAll" :value="true" :indeterminate="!!selectedReservations.length" title="Select all open slots" v-b-tooltip.hover /></th>
                                <th width="100">Time</th>
                                <th>Racer</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(reserv, i) in reservations" :key="i" :class="getTableRowClassForReservation(reserv)">
                                <td>
                                    <b-form-checkbox :value="reserv" v-model="selectedReservations" />
                                </td>

                                <td>{{ timestampTimeFormattedPretty(reserv.get('time')) }}</td>

                                <td v-if="reserv.get('is_blackout')" class="text-center">
                                    <div class="blackout-details">
                                        <small
                                            ><span v-if="reserv.get('notes')"
                                                ><small>{{ reserv.get('notes') }}</small></span
                                            ></small
                                        >
                                    </div>
                                </td>

                                <td v-if="reserv.get('is_blackout')" class="text-right">
                                    <a href="javascript:{}" @click="selectedReservationDetails = reserv" title="View Blackout Details" v-b-tooltip.hover><b-icon icon="info-circle" /></a>
                                    <span v-if="reserv.get('notes') && reserv.get('notes').match(/walk-in/gi)" class="ml-2">
                                        <b-badge variant="success" v-if="reserv.get('paid')"> Paid </b-badge>
                                        <b-badge v-else variant="danger"> Unpaid </b-badge>
                                    </span>
                                </td>

                                <td v-if="!reserv.get('is_blackout')" class="text-right" colspan="2">
                                    <router-link
                                        v-if="reserv.id && (reserv.getTimeContext().isCurrent || reserv.getTimeContext().isNext)"
                                        :to="{ name: 'calendarControl', params: { calendarId: reserv.attributes.calendar }, query: { loginUser: reserv.attributes.user } }"
                                        target="_blank"
                                        class="mr-2"
                                        title="Seat this user and open sim controller"
                                        v-b-tooltip.hover
                                    >
                                        <b-iconstack>
                                            <b-icon stacked icon="controller" />
                                            <b-icon stacked icon="arrow-right-short" shift-h="-14" />
                                            <b-icon stacked icon="person" shift-h="-25" />
                                        </b-iconstack>
                                    </router-link>
                                    <a v-if="seatingGroupIdFor(reserv)" href="javascript:{}" class="mr-1" title="View this group's simulators" v-b-tooltip.hover @click="$emit('viewSeatingGroup', seatingGroupIdFor(reserv))"><b-icon icon="people-fill" /></a>
                                    <a v-if="reserv.id" :href="`${serverConfig.API_BASE_URL}/admin/user/user/${adminUserIdFor(reserv)}`" target="_blank">{{ reserv.get('seated_user_display_name') || reserv.attributes.user_display_name }}</a> &nbsp;
                                    <b-badge variant="success" v-if="reserv.get('user') && reserv.get('paid')"> Paid </b-badge>
                                    <b-badge v-else-if="reserv.get('user') && !reserv.get('paid')" variant="danger"> Unpaid </b-badge>
                                    <b-badge variant="warning" v-if="reserv.get('user_outstanding_waivers') && reserv.get('user_outstanding_waivers').length > 0" class="ml-1">Waivers</b-badge>
                                </td>
                                <!-- <td class="text-right">
                                    <b-dropdown right text="" size="sm" variant="outline-primary" v-if="reserv.get('user')">
                                        <b-dropdown-item v-if="reserv.get('user') && reserv.get('paid') && !reserv.get('is_blackout')" @click="reserv.markAsUnpaid()">Mark as Unpaid</b-dropdown-item>
                                        <b-dropdown-item v-if="reserv.get('user') && !reserv.get('paid') && !reserv.get('is_blackout')" @click="reserv.markAsPaid()">Mark as Paid</b-dropdown-item>
                                        <b-dropdown-item disabled>Charge User {{ currencySymbol() }}{{ moneyFormat(costCents) }}</b-dropdown-item>
                                    </b-dropdown>
                                </td> -->
                            </tr>
                        </tbody>
                    </table>

                    <p v-else-if="!loadingReservationTimes" class="text-center">No Reservations or Availabilities to Show</p>

                    <p v-else class="text-center">Loading...</p>
                </div>
            </b-col>
        </b-row>

        <!-- Modals -->
        <b-modal ref="blackoutModal" size="md" title="Blackout Timeslots" @ok="blackoutDates()" no-fade centered>
            <p>Would you like to blackout the following timeslots?:</p>
            <ul>
                <li v-for="(reserv, i) in selectedReservations" :key="i">{{ reserv }}</li>
            </ul>

            <b-form-group label="Reason">
                <b-form-select v-model="reservationNotesReason">
                    <b-form-select-option v-for="(notesOpt, notesOptKey) in this.reservationNotesReasons" :key="notesOptKey" :value="notesOptKey">{{ notesOpt }}</b-form-select-option>
                </b-form-select>
            </b-form-group>

            <b-form-group v-if="reservationNotesReason == 'WALK_IN'" label="Paid?" description="You can change paid status at any point in the future">
                <b-form-checkbox v-model="reservationNotesPaid">Walk-in has paid already?</b-form-checkbox>
            </b-form-group>

            <b-form-group label="Unlock Simulator for these timeslots?" description="If kiosk mode is enabled, you can unlock simulator with this blackout. Use this for walk-ins and customer service comps">
                <b-form-checkbox v-model="reservationUnlock">Yes, unlock</b-form-checkbox>
            </b-form-group>

            <b-form-group label="Other Notes" :description="reservationNotesReason == 'OTHER' ? `Required if reason is 'Other'` : null">
                <b-textarea placeholder="Add any other notes to blackout" v-model="reservationNotesExtra" />
            </b-form-group>
        </b-modal>

        <b-modal ref="deleteModal" size="md" title="Delete Reservations" @ok="deleteSelectedReservations()" no-fade centered>
            <b-alert variant="danger" show>Are you sure you want to delete the following reservations?</b-alert>
            <h5>{{ calendarResource }}</h5>
            <ul>
                <li v-for="(reserv, i) in selectedReservations" :key="i">
                    <span v-if="reserv.id && !reserv.get('is_blackout')">{{ reserv.attributes.user_display_name }} - </span>{{ reserv }}
                </li>
            </ul>
        </b-modal>

        <b-modal title="Blackout Details" :visible="!!selectedReservationDetails" @hide="selectedReservationDetails = null" ok-only no-fade centered>
            <dl class="row" v-if="selectedReservationDetails && selectedReservationDetails.get('is_blackout')">
                <dt class="col-md-4">Created By</dt>
                <dd class="col-md-8">
                    <router-link v-if="selectedReservationDetails.id" :to="{ name: 'adminUser', params: { userId: selectedReservationDetails.attributes.user } }">
                        {{ selectedReservationDetails.attributes.user_display_name }}
                    </router-link>
                    &nbsp;
                </dd>

                <dt class="col-md-4">Seat</dt>
                <dd class="col-md-8 text-muted">{{ selectedReservationDetails.get('calendar_name') }}</dd>

                <dt class="col-md-4">Paid?</dt>
                <dd class="col-md-8 text-muted">{{ selectedReservationDetails.get('paid') ? 'Yes' : 'No' }}</dd>

                <dt class="col-md-4">Unlocks?</dt>
                <dd class="col-md-8 text-muted">{{ selectedReservationDetails.get('is_kiosk_mode_unlock') ? 'Yes' : 'No' }}</dd>

                <dt class="col-md-4">Time</dt>
                <dd class="col-md-8 text-muted">{{ selectedReservationDetails }}</dd>

                <dt class="col-md-4">Created</dt>
                <dd class="col-md-8 text-muted">{{ timestampDayFormattedPretty(selectedReservationDetails.get('created')) }} @ {{ timestampTimeFormattedPretty(selectedReservationDetails.get('created')) }}</dd>

                <dt class="col-md-4">Notes</dt>
                <dd class="col-md-8">{{ selectedReservationDetails.get('notes') }}</dd>
            </dl>
        </b-modal>
    </div>
</template>

<style lang="scss">
#time-pane-planner {
    max-height: 580px;
    overflow-y: scroll;

    table {
        width: 100%;

        tr {
            height: 40px;

            &.empty,
            &.blackout {
                td {
                    color: #ccc;
                }
            }

            &.blackout {
                .blackout-details {
                    overflow: hidden !important;
                    height: 23px;
                }
            }

            &.reserved {
                border-left: 3px solid #34beed;
                font-weight: bold;

                a {
                    font-weight: normal;
                }
            }

            &.current {
                background-color: #34beed1c;
            }

            &.next {
                background-color: #34beed1c;
                border-top: 3px solid #34beed;
            }
        }
    }
}

.time-context .badge a {
    color: #fff;
}
</style>
